/**
 * What a capability would show, and which bundle it belongs to.
 *
 * The house pattern for an unentitled feature is an `<Alert severity="info">`
 * saying the feature is not enabled — see `ComponentsView` and
 * `CostAnalysisPage`. `useCanContactVendor`'s own docstring explains the rule
 * that pattern follows: *"a feature that silently disappears reads as a
 * product that is broken; only the suggested action changes."*
 *
 * This goes one step further, by decision: show the SHAPE of the answer behind
 * a blur, so a reader can see what they would get rather than only read that
 * they do not have it.
 *
 * Why a blur here cannot leak anything
 * ------------------------------------
 * Every gated route 404s without its entitlement, so there is no payload to
 * blur. A teaser that fetched real rows and blurred them client-side would be
 * an entitlement bypass — the data would sit in the browser for anyone with
 * devtools. The architecture rules that out rather than our care doing it, and
 * that is the right way round.
 *
 * Why there are no numbers behind the blur
 * ----------------------------------------
 * Blurred shapes are read as data. A screenshot of "14 reachable paths" behind
 * a blur is a fabricated finding with a soft edge, and the rule against
 * inventing scan output is absolute — invented counts and identifiers imply
 * first-hand data we do not have, and collapse the moment a prospect probes
 * them.
 *
 * So the skeleton carries **column headers only** — those are product
 * vocabulary, not anybody's estate — over featureless bars. No digits, no
 * identifiers, no verdict values. The caption says it is illustrative, in
 * readable text rather than behind the blur, so a screenshot cannot lose it.
 */
import {
  Box, Button, Card, CardContent, Chip, Typography,
} from '@mui/material'
import { Lock } from '@mui/icons-material'
import type { ReactNode } from 'react'
import { BundleCopy } from './bundleCopy'

/** The bundles a teaser can advertise. Typed rather than a free string so a
 *  typo cannot invent a SKU that sales does not sell. */
export type Bundle = 'ARIAS Exposure' | 'ARIAS Evidence'

interface BundleTeaserProps {
  /** Copy from `bundleCopy`, looked up by the same feature key the gate
   *  refuses on. Carries the consequence first, the mechanism second — the
   *  earlier version took a single `answers` sentence and every call site
   *  wrote a definition, which tells a reader what the feature IS and never
   *  why they should want it. */
  copy: BundleCopy
  /** From `useCanContactVendor`. Only the suggested ACTION varies by role —
   *  what is shown does not. */
  canContactVendor: boolean
  /** Rows of placeholder bars. Enough to read as a table, few enough not to
   *  look like a real result set. */
  rows?: number
  /** Shown INSTEAD of the blurred skeleton. For a capability whose value is
   *  the content rather than the shape — a certificate is persuasive because
   *  of the fields on it, and blurring them hides the only part that sells. */
  specimen?: ReactNode
  /** Smaller type and tighter spacing, for several teasers side by side.
   *  Four full-size ones stacked would bury the findings a tenant DOES hold,
   *  which is an advert displacing the product. */
  compact?: boolean
}

const BundleTeaser = ({
  copy, canContactVendor, rows = 4, specimen, compact,
}: BundleTeaserProps) => (
  <Box sx={{ py: compact ? 0.5 : 1 }}>
    <Box sx={{ textAlign: compact ? 'left' : 'center', maxWidth: 620, mx: 'auto', mb: compact ? 1 : 2 }}>
      <Chip
        icon={<Lock sx={{ fontSize: 16 }} />}
        label={copy.bundle}
        size="small"
        sx={{ mb: 1, fontWeight: 600 }}
      />
      {/* Consequence first. This is the line that has to land. */}
      <Typography
        variant={compact ? 'body2' : 'subtitle1'}
        sx={{ fontWeight: 600, mb: compact ? 0.75 : 1 }}
      >
        {copy.headline}
      </Typography>
      {/* Mechanism immediately after, so the headline is not an assertion the
          reader has to take on trust. */}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {copy.detail}
      </Typography>
      {/* The question somebody else will ask them. The absence hurts at the
          moment it is asked, not while reading a feature list. */}
      <Typography
        variant="caption"
        display="block"
        sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1.25 }}
      >
        Without it: {copy.without}
      </Typography>
      {canContactVendor ? (
        <Button
          size="small"
          variant="contained"
          href={`mailto:sales@tryarias.com?subject=${encodeURIComponent(copy.bundle)}`}
        >
          Talk to us about {copy.bundle}
        </Button>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block">
          Ask your tenant administrator about {copy.bundle}.
        </Typography>
      )}
    </Box>

    {specimen || (
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        {/* The skeleton. `aria-hidden` because placeholder bars are decoration
            — a screen reader announcing eight empty cells would be worse than
            silence — and `userSelect: none` so the headers cannot be copied
            out of context as if they were a result.

            Still no digits, identifiers or verdict values: blurred shapes are
            read as data, and a screenshot of "14 reachable paths" behind a
            blur is a fabricated finding with a soft edge. Column headers are
            product vocabulary, not anybody's estate. */}
        <Box
          aria-hidden="true"
          sx={{
            filter: 'blur(3.5px)',
            opacity: 0.55,
            userSelect: 'none',
            pointerEvents: 'none',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            {copy.columns.map((c) => (
              <Typography
                key={c}
                variant="caption"
                sx={{ flex: 1, fontWeight: 700, color: 'text.secondary' }}
              >
                {c}
              </Typography>
            ))}
          </Box>
          {Array.from({ length: rows }).map((_, row) => (
            <Box key={row} sx={{ display: 'flex', gap: 2, mb: 1.25 }}>
              {copy.columns.map((c, col) => (
                <Box
                  key={c}
                  sx={{
                    flex: 1,
                    height: 12,
                    borderRadius: 6,
                    bgcolor: 'action.hover',
                    // Varied widths so it reads as a table rather than a grid
                    // of identical blocks. Derived from position, not data.
                    width: `${70 + ((row * 7 + col * 13) % 30)}%`,
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    )}

    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ textAlign: 'center', mt: 1 }}
    >
      Illustrative layout — not your estate.
    </Typography>
  </Box>
)

export default BundleTeaser
